import { MotifIcon } from "@ey-xd/motif-react";
import { IconoirWarningCircle } from "@ey-xd/motif-icon";
import "./OrderTable.scss";
import { ProductAccordian } from "../../../pages/Checkout/Components/OrderExpected/ProductAccordian";
import OrderItemsHeaderTooltip from "../../OrderPipeline/OrderItemsHeaderTooltip/OrderItemsHeaderTooltip";
import { useRef, useState, useEffect } from "react";
export interface ColumnConfig {
  key: string;
  label: string;
  width: string;
  render?: (row: any) => React.ReactNode;
}

interface OrderDetail {
  orderParentId: string;
  isExpanded: boolean;
}

interface OrdersTableProps {
  data: any[];
  orderDetails: OrderDetail[];
  columnsData: ColumnConfig[];
  showEngagementColumn?: boolean;
  retiredSkuList?: { [x: number]: string[] }[];
  showRetiredError?: boolean;
}

const OrdersTable: React.FC<OrdersTableProps> = ({
  data,
  columnsData,
  orderDetails,
  showEngagementColumn = false,
  retiredSkuList = [],
  showRetiredError = false,
}) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [fixedHeader, setFixedHeader] = useState(false);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  const [expandedTableWidth, setExpandedTableWidth] = useState<number>(0);

  const isHavingSkuRetired = (currentOrderId: number) => {
    let isExist = false;
    retiredSkuList.forEach((retiredSkuOrder) => {
      const retiredSkuKey = Number(Object.keys(retiredSkuOrder)[0]);
      if (retiredSkuKey === currentOrderId) {
        isExist = true;
      }
    });
    return isExist;
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!tableRef?.current) return;
      const tableTop = tableRef?.current.getBoundingClientRect().top;
      setFixedHeader(tableTop < 0);
    };
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  useEffect(() => {
    const measureColumnWidths = () => {
      if (!tableRef.current) return;
      const firstRow = tableRef.current.querySelector("tr");
      if (!firstRow) return;
      const widths: number[] = Array.from(firstRow.children).map(
        (cell: any) => cell.getBoundingClientRect().width,
      );
      setColumnWidths(widths);
    };

    measureColumnWidths();
    window.addEventListener("resize", measureColumnWidths);

    const resizeObserver = new ResizeObserver(measureColumnWidths);
    if (tableRef.current) {
      resizeObserver.observe(tableRef.current);
    }

    return () => {
      window.removeEventListener("resize", measureColumnWidths);
      resizeObserver.disconnect();
    };
  }, [data, columnsData]);

  useEffect(() => {
    const updateExpandedTableWidth = () => {
      if (!tableScrollRef.current) return;
      setExpandedTableWidth(tableScrollRef.current.clientWidth);
    };

    updateExpandedTableWidth();
    window.addEventListener("resize", updateExpandedTableWidth);

    const resizeObserver = new ResizeObserver(updateExpandedTableWidth);
    if (tableScrollRef.current) {
      resizeObserver.observe(tableScrollRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateExpandedTableWidth);
      resizeObserver.disconnect();
    };
  }, [data, columnsData]);

  const syncScroll = () => {
    if (tableScrollRef.current) {
      const scrollLeft = tableScrollRef.current.scrollLeft;
      const fixedHeaderTable =
        document.querySelector<HTMLTableElement>(".fixed-header");
      if (fixedHeaderTable) {
        fixedHeaderTable.style.transform = `translateX(-${scrollLeft}px)`;
      }
    }
  };

  return (
    <div className="order-table-container" style={{ position: "relative" }}>
      {fixedHeader && (
        <div
          className="fixed-header-wrapper"
          style={{
            overflow: "hidden",
            width: tableScrollRef.current?.getBoundingClientRect().width,
          }}
        >
          <table
            className="motif-table-standard fixed-header"
            style={{ width: columnWidths.reduce((a, b) => a + b, 0) }}
          >
            <thead>
              <tr>
                {columnsData.map((col, index) => (
                  <th key={col.key} style={{ width: columnWidths[index] }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </div>
      )}
      <div
        className="table-scroll-wrapper"
        ref={tableScrollRef}
        style={{ overflowX: "auto" }}
        onScroll={syncScroll}
      >
        <table className="motif-table-standard" ref={tableRef}>
          <thead>
            <tr>
              {columnsData.map((col) => (
                <th
                  key={col.key}
                  style={{ minWidth: "160px" }}
                  className={
                    col.key === "Total" || col.key === "totalCost"
                      ? "text-right-align"
                      : ""
                  }
                >
                  {col.label}{" "}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.map((order) => (
              <>
                <tr>
                  {columnsData.map((col) => (
                    <td key={col.key}>
                      <div
                        className={
                          col.key === "orderName"
                            ? "sku-retired-grid-workspace"
                            : ""
                        }
                      >
                        {col.key === "orderName" &&
                          showRetiredError &&
                          isHavingSkuRetired(order.OrderParentId) && (
                            <MotifIcon
                              src={IconoirWarningCircle}
                              className="warning-icon"
                            />
                          )}
                        {col.render ? col.render(order) : order[col.key]}
                      </div>
                    </td>
                  ))}
                </tr>

                <tr
                  className={
                    orderDetails.find(
                      (orderDetail: any) =>
                        orderDetail.orderParentId === order.OrderParentId,
                    )?.isExpanded
                      ? "product-list-wrapper"
                      : "product-list-wrapper hide-details"
                  }
                >
                  <td
                    colSpan={columnsData.length}
                    className="product-list order-success-content"
                  >
                    <>
                      <div className="product-list-content">
                        <table
                          className={`motif-table-standard ${showEngagementColumn ? "with-engagement-col" : "without-engagement-col"}`}
                          style={
                            expandedTableWidth
                              ? {
                                  width: `${expandedTableWidth}px`,
                                  maxWidth: `${expandedTableWidth}px`,
                                }
                              : undefined
                          }
                        >
                          <thead>
                            <tr>
                              <th className="order-items-sku-col">
                                <OrderItemsHeaderTooltip
                                  label="SKU name/SKU ID"
                                  type="sku"
                                />
                              </th>
                              {showEngagementColumn && (
                                <th className="order-items-engagement-col">
                                  Engagement code
                                </th>
                              )}
                              <th className="text-right-align order-items-unit-cost-col">
                                <OrderItemsHeaderTooltip
                                  label="Unit cost"
                                  type="unitCost"
                                />
                              </th>
                              <th className="order-items-units-col">
                                <OrderItemsHeaderTooltip
                                  label="Units per period"
                                  type="unitsPerPeriod"
                                />
                              </th>
                              <th className="order-items-periods-col">
                                <OrderItemsHeaderTooltip
                                  label="No. of periods"
                                  type="numberOfPeriods"
                                />
                              </th>
                              <th className="text-right-align order-items-total-col">
                                <OrderItemsHeaderTooltip
                                  label="Total cost"
                                  type="totalCost"
                                />
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {order.Details &&
                              order.Details.length > 0 &&
                              order.Details.map((detail: any) => {
                                return (
                                  <ProductAccordian
                                    key={detail.ProductName}
                                    productName={detail.ProductName}
                                    skuDetails={detail.SkuDetails}
                                    showEngagementColumn={showEngagementColumn}
                                    showRetiredError={showRetiredError}
                                    isOrderpipelineOrderTab={true}
                                    showQuantityColumn={false}
                                  />
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  </td>
                </tr>
              </>
            ))}
            {data.length === 1 && <div className="spacer-myworkspace"></div>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrdersTable;
